import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, ArrowLeft, Search, Ghost, ShieldCheck, 
  MessageSquare, MessageSquarePlus, Loader2, ChevronRight, X,
  MoreVertical, Trash2, Ban, Unlock, ShieldAlert
} from 'lucide-react';
import { db, auth } from '../firebase';
import { 
  collection, query, where, onSnapshot, orderBy, limit,
  doc, updateDoc, arrayUnion, arrayRemove, serverTimestamp
} from 'firebase/firestore';

export const SavedChatsPage = ({ isEmbedded = false, onBackToMode }) => {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [chatLimit, setChatLimit] = useState(20);
  
  // Action Menu State
  const [selectedChat, setSelectedChat] = useState(null);
  
  const myUid = auth.currentUser?.uid;

  useEffect(() => {
    if (!myUid) {
      navigate('/login');
      return;
    }

    // Query chats where I am a participant and saved === true
    const q = query(
      collection(db, 'anonymousChats'),
      where('participants', 'array-contains', myUid),
      where('saved', '==', true),
      orderBy('lastMessageTime', 'desc'),
      limit(chatLimit)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedChats = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setChats(fetchedChats);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [myUid, navigate, chatLimit]);

  const handleScroll = (e) => {
      const bottom = Math.ceil(e.target.scrollHeight - e.target.scrollTop) === e.target.clientHeight;
      if (bottom && chats.length >= chatLimit) {
          setChatLimit(prev => prev + 20);
      }
  };

  const filteredChats = chats.filter(chat => {
      const isUser1 = chat.user1 === myUid;
      const unreadCount = isUser1 ? chat.unreadCount_user1 : chat.unreadCount_user2;
      
      // Smart Hidden Check
      if (chat.hiddenFor?.includes(myUid)) {
          if (unreadCount > 0) {
              // Auto-recover on next click or render
              updateDoc(doc(db, 'anonymousChats', chat.id), { 
                  hiddenFor: arrayRemove(myUid) 
              }).catch(()=>{});
          } else {
              return false;
          }
      }

      const partnerAlias = isUser1 ? chat.user2Alias : chat.user1Alias;
      return partnerAlias?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Actions
  const openActionMenu = (e, chat) => {
      e.stopPropagation();
      setSelectedChat(chat);
  };

  const closeActionMenu = () => {
      setSelectedChat(null);
  };

  const deleteChatForMe = async () => {
      if (!selectedChat) return;
      const chatId = selectedChat.id;
      closeActionMenu();

      try {
          const fieldName = `clearedAt_${myUid}`;
          await updateDoc(doc(db, 'anonymousChats', chatId), {
              hiddenFor: arrayUnion(myUid),
              [fieldName]: serverTimestamp()
          });
      } catch (e) {
        //   console.error(e);
      }
  };

  const toggleBlockUser = async () => {
      if (!selectedChat) return;
      const chatId = selectedChat.id;
      const isUser1 = selectedChat.user1 === myUid;
      const isBlocked = selectedChat.blockedBy?.includes(myUid);
      closeActionMenu();

      try {
          if (isBlocked) {
              await updateDoc(doc(db, 'anonymousChats', chatId), {
                  blockedBy: arrayRemove(myUid)
              });
          } else {
              await updateDoc(doc(db, 'anonymousChats', chatId), {
                  blockedBy: arrayUnion(myUid)
              });
          }
      } catch (e) {
        //   console.error(e);
      }
  };

    if (loading) {
        return (
            <div className={`${isEmbedded ? 'h-full' : 'min-h-screen w-full sm:w-[700px] border-r border-gray-800'} bg-black flex items-center justify-center`}>
                <Loader2 className="w-10 h-10 text-[#0BD9E5] animate-spin" />
            </div>
        );
    }

    return (
      <div className={`${isEmbedded ? 'h-full' : 'min-h-screen w-full sm:w-[700px] border-r border-gray-800 pb-24 sm:pb-0 shrink-0'} bg-[#050508] font-poppins text-white flex flex-col relative overflow-hidden`}>
      
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(11,217,229,0.08),transparent_70%)]" />
      </div>

      {/* Header */}
      <div className="relative z-10 px-4 py-6 bg-black/50 backdrop-blur-md border-b border-white/10 shrink-0">
          <div className="flex items-center gap-4 mb-6 w-full">
             
                 <button onClick={() => { if (isEmbedded && onBackToMode) onBackToMode(); else navigate(-1); }} className="p-2 hover:bg-white/10 rounded-full transition-colors shrink-0" aria-label="Back">
                    <ChevronLeft size={28} className="text-white" />
                 </button>

             <div className="flex items-center justify-between flex-1 min-w-0 pr-2">
                <h1 className="text-2xl font-black tracking-tight truncate">Your Connections</h1>
                <button 
                    onClick={() => {
                      if (onBackToMode) onBackToMode();
                      navigate('/chat');
                    }} 
                    className="hidden sm:flex items-center justify-center w-10 h-10 bg-white/5 hover:bg-white/10 text-white rounded-full transition-colors border border-white/10 shrink-0"
                    title="New Chat / Select Mode"
                >
                    <MessageSquarePlus size={20} className="text-[#0BD9E5]" />
                </button>
             </div>
          </div>

          {/* Search Bar */}
          <div className="relative flex items-center bg-[#111] border border-gray-800 focus-within:border-[#0BD9E5]/50 rounded-2xl px-4 py-3 transition-colors">
              <Search size={18} className="text-gray-500 mr-3 shrink-0" />
              <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by shadow name..."
                  className="bg-transparent border-none text-white w-full focus:outline-none text-sm placeholder:text-gray-600"
              />
              {searchQuery && (
                 <button onClick={() => setSearchQuery('')} className="ml-2 text-gray-500 hover:text-white">
                    <X size={16} /></button>)}</div>
      </div>

      {/* Chat List */}
      <div 
          className="relative z-10 flex-1 overflow-y-auto px-4 py-4 space-y-3 no-scrollbar"
          onScroll={handleScroll}
      >
          <AnimatePresence>
              {filteredChats.length === 0 ? (
                  <motion.div 
                     initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                     className="flex flex-col items-center justify-center pt-20 text-center opacity-60"
                  >
                     <MessageSquare size={48} className="text-gray-600 mb-4" strokeWidth={1.5} />
                     <p className="text-lg font-bold text-white mb-1">No connections active</p>
                     <p className="text-sm text-gray-400 max-w-[250px]">
                        Save a chat while in anonymous mode to connect with them here forever.
                     </p>
                  </motion.div>
              ) : (
                  filteredChats.map((chat, idx) => {
                      const isUser1 = chat.user1 === myUid;
                      const partnerAlias = isUser1 ? chat.user2Alias : chat.user1Alias;
                      const partnerCollege = isUser1 ? chat.user2College : chat.user1College;
                      const unreadCount = isUser1 ? chat.unreadCount_user1 : chat.unreadCount_user2;
                      
                      // Format time (naive for MVP - can use date-fns if preferred later)
                      const timeString = chat.lastMessageTime?.toDate() 
                         ? new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: 'numeric' }).format(chat.lastMessageTime.toDate())
                         : '';

                      return (
                          <motion.div
                              key={chat.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: idx * 0.05 }}
                              onClick={() => navigate(isEmbedded ? `/chat/room/${chat.id}` : `/anonymous-chat/${chat.id}`, { state: { chatData: chat } })}
                              className="bg-[#111] border border-gray-800 hover:border-[#0BD9E5]/30 rounded-2xl p-4 flex items-center justify-between cursor-pointer group transition-all"
                          >
                              <div className="flex items-center gap-4 flex-1 min-w-0">
                                  <div className="relative shrink-0">
                                      <div className={`w-14 h-14 rounded-full bg-gray-900 border-2 flex items-center justify-center overflow-hidden relative ${unreadCount > 0 ? 'border-[#0BD9E5]' : 'border-gray-700'}`}>
                                          <img 
                                              src={`/avatars/${partnerAlias}.png`}
                                              alt={partnerAlias} 
                                              className="w-full h-full object-cover"
                                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                                          />
                                          <Ghost size={24} className="text-gray-400 absolute hidden" />
                                      </div>
                                      {unreadCount > 0 && (
                                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#0BD9E5] rounded-full border-2 border-[#111] flex items-center justify-center">
                                              <span className="text-[10px] font-bold text-black">{unreadCount}</span>
                                          </div>
                                      )}
                                  </div>
                                  
                                  <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-1">
                                          <h3 className="font-bold text-base text-white flex items-center gap-1.5 flex-1 min-w-0 pr-2">
                                              <span className="shrink-0 truncate max-w-[140px]">{partnerAlias}</span>
                                              <ShieldCheck size={14} className="text-green-500 shrink-0" />
                                              <span className="px-2 py-0.5 ml-1 bg-gray-900 border border-gray-700 rounded-full text-[10px] font-bold text-gray-400 truncate shrink">
                                                 {partnerCollege || 'Unknown'}
                                              </span>
                                          </h3>
                                      </div>
                                      <p className={`text-sm truncate ${(chat.blockedBy?.length > 0) ? 'text-red-400 font-semibold' : unreadCount > 0 ? 'text-white font-semibold' : 'text-gray-400'}`}>
                                          {(chat.blockedBy?.length > 0) ? 'Connection Paused' : chat.lastMessage || 'Connected'}
                                      </p>
                                  </div>
                              </div>
                              
                              <div className="flex flex-col items-end shrink-0 ml-2 justify-between self-stretch py-0.5">
                                  <span className="text-[10px] text-gray-500 font-medium">{timeString}</span>
                                  <button 
                                      onClick={(e) => openActionMenu(e, chat)}
                                      className="p-1 -mr-1 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
                                  >
                                      <MoreVertical size={18} />
                                  </button>
                              </div>
                          </motion.div>
                      );
                  })
              )}
          </AnimatePresence>
          
          {chats.length >= chatLimit && (
              <div className="w-full flex justify-center py-4">
                  <Loader2 size={24} className="text-gray-600 animate-spin" />
              </div>
          )}

      </div>

      {/* Action Modal */}
      <AnimatePresence>
          {selectedChat && (
              <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                  <motion.div 
                      className="absolute inset-0"
                      onClick={closeActionMenu}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                  />
                  <motion.div 
                      initial={{ opacity: 0, y: 100 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 100 }}
                      className="w-full max-w-sm bg-[#111] border border-gray-800 rounded-3xl shadow-2xl overflow-hidden relative z-10"
                  >
                      {/* Drag handle indicator (mobile) */}
                      <div className="w-12 h-1.5 bg-gray-800 rounded-full mx-auto my-3 sm:hidden" />
                      
                      <div className="px-6 py-4 border-b border-gray-800 flex items-center gap-4">
                          <div className="w-12 h-12 shrink-0 rounded-full bg-gray-900 border border-gray-700 flex items-center justify-center overflow-hidden">
                              <img 
                                  src={`/avatars/${(selectedChat.user1 === myUid ? selectedChat.user2Alias : selectedChat.user1Alias)}.png`}
                                  alt="Avatar" 
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                              />
                              <Ghost size={20} className="text-gray-500 absolute hidden" />
                          </div>
                          <div>
                              <h3 className="font-bold text-white text-lg">
                                  {selectedChat.user1 === myUid ? selectedChat.user2Alias : selectedChat.user1Alias}
                              </h3>
                              <p className="text-xs text-gray-400">Manage connection</p>
                          </div>
                      </div>

                      <div className="p-2">
                          <button 
                              onClick={deleteChatForMe}
                              className="w-full flex items-center gap-3 px-4 py-4 hover:bg-white/5 rounded-xl transition-colors text-left text-white"
                          >
                              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                                  <Trash2 size={20} className="text-red-500" />
                              </div>
                              <div>
                                  <p className="font-semibold text-red-500">Delete Chat</p>
                                  <p className="text-xs text-gray-500">Remove from your list</p>
                              </div>
                          </button>

                          <button 
                              onClick={toggleBlockUser}
                              className="w-full flex items-center gap-3 px-4 py-4 hover:bg-white/5 rounded-xl transition-colors text-left text-white mt-1"
                          >
                              <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0">
                                  {selectedChat.blockedBy?.includes(myUid) ? (
                                      <Unlock size={20} className="text-orange-500" />
                                  ) : (
                                      <Ban size={20} className="text-orange-500" />
                                  )}
                              </div>
                              <div>
                                  <p className="font-semibold text-orange-500">
                                      {selectedChat.blockedBy?.includes(myUid) ? 'Unblock User' : 'Block User'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                      {selectedChat.blockedBy?.includes(myUid) ? 'Allow new messages again' : 'Stop receiving messages'}
                                  </p>
                              </div>
                          </button>
                      </div>

                      <div className="p-4 pt-0">
                          <button 
                              onClick={closeActionMenu}
                              className="w-full py-3.5 bg-white/10 hover:bg-white/15 text-white font-bold rounded-2xl transition-colors"
                          >
                              Cancel
                          </button>
                      </div>
                  </motion.div>
              </div>
          )}
      </AnimatePresence>

    </div>
  );
};

export default SavedChatsPage;
